import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import dayjs from 'dayjs';
import useFetchApi from '../../../hooks/useFetchApi';
import { GetIrrigationGroupingResponse } from '../../../requests/api/apiTypes';
import apiClient from '../../../requests/api/apiClient';
import mapDashboardData from './dashboardMapper';
import { buildEquipoGrouping, getIrrigatingSectorIds } from './equipoColors';
import type { SectorLike, UseEquipoGroupingResult } from './riego.types';

const useEquipoGrouping = (
  farmId: string | undefined,
  sectors: SectorLike[] | undefined,
): UseEquipoGroupingResult => {
  const { initTime, endTime } = useMemo(() => {
    const start = dayjs().startOf('day');
    return {
      initTime: start.toISOString(),
      endTime: start.add(1, 'day').toISOString(),
    };
  }, []);

  const { data: groupingData, loading } = useFetchApi<GetIrrigationGroupingResponse>({
    id: farmId || '',
    dependencies: [farmId || ''],
    modelName: 'irrigation',
    modelEndpoint: 'getIrrigationGrouping',
  });

  const grouping = useMemo(
    () => buildEquipoGrouping(groupingData),
    [groupingData],
  );

  const [irrigatingSectorIds, setIrrigatingSectorIds] = useState<Set<string>>(new Set());

  const sectorsKey = useMemo(
    () => (sectors || []).map((s) => s.wiseconnId || s.id || '').join(','),
    [sectors],
  );
  const sectorsRef = useRef(sectors);
  sectorsRef.current = sectors;

  useEffect(() => {
    let isMounted = true;
    setIrrigatingSectorIds(new Set());
    const currentSectors = sectorsRef.current;
    if (!farmId || !currentSectors?.length) return undefined;

    const loadIrrigating = async () => {
      try {
        const { data } = await apiClient.irrigation.getIrrigations(
          farmId,
          { initTime, endTime },
        );
        if (!isMounted || !data) return;
        setIrrigatingSectorIds(getIrrigatingSectorIds(mapDashboardData(data), currentSectors));
      } catch {
        // Fallo silencioso: sin datos de Wiseconn no hay resaltado
      }
    };
    loadIrrigating();

    return () => {
      isMounted = false;
    };
  }, [farmId, initTime, endTime, sectorsKey]);

  return { grouping, irrigatingSectorIds, loading };
};

export default useEquipoGrouping;
